import type { CSSProperties } from 'react';
import { useActivityStatus } from '../../hooks/useActivityStatus';

/**
 * "Active Recently" badge for Discover cards: active within the last 48 hours.
 *
 * Sits flat inside the top-left corner of the card (the country flag has the
 * top right). Kept inside the card bounds on purpose: the horizontal scroll
 * decks clip anything that overhangs the card top. Put it inside a
 * `position: relative` card wrapper.
 *
 * Renders nothing when the user is not recently active, or when their status is
 * not visible to the viewer (hidden, reciprocal hide, blocked); the hook handles
 * all of that.
 *
 * Styled inline: src/index.css is precompiled Tailwind.
 */
export function RecentlyActiveBadge({
  userId,
  style,
}: {
  userId?: string | number | null;
  style?: CSSProperties;
}) {
  const { isRecentlyActive } = useActivityStatus(userId);
  if (!isRecentlyActive) return null;

  return (
    <div
      aria-label="Active recently"
      className="pointer-events-none"
      style={{
        position: 'absolute',
        top: 6,
        left: 6,
        zIndex: 20,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.45em',
        padding: '3px 7px',
        borderRadius: 999,
        // 50% opaque so the photo shows through; text and dot stay fully opaque.
        background: 'linear-gradient(135deg, rgba(96,165,250,0.5) 0%, rgba(29,78,216,0.5) 100%)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
        color: '#ffffff',
        // Brand display face (@font-face in src/index.css). Condensed and
        // single-weight, so a little larger and no bold, which would fake-bold it.
        fontFamily: "'AmericanCaptain', 'Poppins', sans-serif",
        fontSize: 11,
        fontWeight: 400,
        lineHeight: 1.1,
        letterSpacing: 0.5,
        whiteSpace: 'nowrap',
        // Subtle dark-gray outline: half-pixel offsets at partial opacity, so it
        // softens the letter edges against the blue without looking stroked.
        textShadow:
          '-0.5px -0.5px 0 rgba(20,20,20,0.7), 0.5px -0.5px 0 rgba(20,20,20,0.7), ' +
          '-0.5px 0.5px 0 rgba(20,20,20,0.7), 0.5px 0.5px 0 rgba(20,20,20,0.7)',
        ...style,
      }}
    >
      <GlowDot />
      Active Recently
    </div>
  );
}

/**
 * The glowing green activity dot, shared by the badge and the profile modal.
 * Sized in em so it follows the surrounding text.
 *
 * Solid bright green with a glow. No dark ring: at small sizes a border
 * swallowed the fill and the dot read as black.
 */
export function GlowDot({ style }: { style?: CSSProperties }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'block',
        width: '0.8em',
        height: '0.8em',
        minWidth: 7,
        minHeight: 7,
        borderRadius: '50%',
        flexShrink: 0,
        background: '#4ADE80',
        boxShadow: '0 0 4px 1px rgba(74,222,128,0.95), 0 0 9px 3px rgba(34,197,94,0.65)',
        ...style,
      }}
    />
  );
}
